
import { Clock, FileText, GitBranch, LineChart, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../Button";

const CaseManagementContent = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="mb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">Case Management</h1>
        <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto">
          AI-powered case management systems offer advanced features to increase efficiency, improve accuracy, and enhance client outcomes.
        </p>
      </div>

      {/* Overview Section */}
      <div className="mb-20">
        <div className="prose prose-lg prose-invert max-w-4xl mx-auto">
          <p>
            AI-powered case management systems offer a range of features and potential benefits to legal professionals including increased efficiency, improved accuracy, cost savings, and enhanced client outcomes. By automating repetitive tasks, legal professionals can focus on high-value strategic work while maintaining better organization and compliance.
          </p>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-blue-400">
              <FileText size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Document Analysis and Summarization</h3>
            <p className="text-white/70">
              AI tools can quickly review, categorize, and summarize case documents, saving time and ensuring no critical details are overlooked.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-purple-400">
              <Clock size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Workflow Automation</h3>
            <p className="text-white/70">
              These systems streamline administrative tasks like case intake, compliance tracking, and court filings, reducing manual effort and human error.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-green-400">
              <LineChart size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Strategic Insights</h3>
            <p className="text-white/70">
              AI analyzes historical data to provide actionable insights, identify patterns, and support case strategy development.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-amber-400">
              <GitBranch size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Chronology Building</h3>
            <p className="text-white/70">
              AI organizes case timelines by linking facts, events, and evidence into structured narratives for efficient trial preparation.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-red-400">
              <MessageSquare size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Client Communication</h3>
            <p className="text-white/70">
              Automated updates and sentiment analysis improve client satisfaction by maintaining proactive communication.
            </p>
          </div>
        </div>
      </div>

      {/* Case Studies Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Curated Case Studies</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Caseload Management</h3>
            <p className="text-white/70 mb-4">
              How C&B Law Group Tripled Their Caseload with Filevine
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.filevine.com/customers/case-studies/how-cb-law-group-tripled-their-caseload-with-filevine/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Business Growth</h3>
            <p className="text-white/70 mb-4">
              How Gluckstein Lawyers doubled firm size and tripled revenue by focusing on the business side of law
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.filevine.com/customers/case-studies/gluckstein-grows-his-firm-amidst-covid-thanks-to-filevine/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Client Deliverables</h3>
            <p className="text-white/70 mb-4">
              How Attorneys at Cuatrecasas are Enhancing Client Deliverables with Harvey
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.harvey.ai/customers/cuatrecasas', '_blank')}
            >
              Read Study
            </Button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-6">Optimize Your Case Management</h2>
        <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
          Learn how our AI-powered case management solutions can transform your legal practice today.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact">
            <Button variant="glass" size="lg" hasAnimation={true}>Contact Us</Button>
          </Link>
          <Link to="/services/advisory">
            <Button variant="outline" size="lg">Explore Advisory Services</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CaseManagementContent;
